import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import SlideOver from "@/components/SlideOver";
import MagicWand from "@/components/MagicWand";
import GiftPreview from "@/components/GiftPreview";
import MaisonWallet from "@/components/MaisonWallet";
import {
  Cake, Sparkles, ArrowRight, Send, Save, RefreshCw, Clock, Heart, Truck,
  Wand2, Gift, ExternalLink, Plus, FileText, CheckCircle2, MapPin,
} from "lucide-react";

const TONES = ["warm and heartfelt", "funny and playful", "elegant and poetic", "sincere and simple"];
const COLORS = ["#8a6cae", "#5a8c72", "#c9a24a", "#b06a8e", "#6a7fb0"];

const occasionTint = (name) => {
  const tints = [
    "from-[#efe3d0] to-[#e6d3b8]",
    "from-[#e3ded0] to-[#d8e0d0]",
    "from-[#efe0e6] to-[#e6d3dd]",
    "from-[#e0e2ef] to-[#d5d9ec]",
  ];
  let h = 0; for (const ch of (name || "")) h += ch.charCodeAt(0);
  return tints[h % tints.length];
};

const CommandCenter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [milestones, setMilestones] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [cards, setCards] = useState([]);
  const [lovedOnes, setLovedOnes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [panel, setPanel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draftMsg, setDraftMsg] = useState("");
  const [craftTone, setCraftTone] = useState(TONES[0]);
  const [craftResult, setCraftResult] = useState(null);
  const [previewId, setPreviewId] = useState(null);

  // Add Loved One drawer
  const [addOpen, setAddOpen] = useState(false);
  const [nf, setNf] = useState({ name: "", relationship: "", personality: "", interests: "", sd_label: "Birthday", sd_date: "", avatar_color: COLORS[0], pinterest_url: "", dob: "" });
  const [savingLo, setSavingLo] = useState(false);

  const load = useCallback(async () => {
    const [m, d, c, lo] = await Promise.all([
      api.get("/milestones"), api.get("/drafts"), api.get("/cards"), api.get("/loved-ones"),
    ]);
    setMilestones(m.data); setDrafts(d.data); setCards(c.data); setLovedOnes(lo.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Navigation & Discovery — the Sidebar's "Maison Wallet" link jumps straight to #wallet.
  useEffect(() => {
    if (window.location.hash === "#wallet") {
      const el = document.getElementById("wallet");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [loading]);

  const loById = Object.fromEntries(lovedOnes.map((l) => [l.id, l]));
  const upcoming = milestones.filter((m) => m.days_until <= 30).slice(0, 8);
  const recentlySent = cards.filter((c) => c.delivery && ["printing", "mailed", "in_transit", "delivered"].includes(c.delivery.status)).slice(0, 8);

  const draftForMilestone = (m) => drafts.find((d) => d.loved_one_id === m.loved_one_id && d.occasion === m.occasion);

  // ---- panel actions ----
  const openReview = (d) => { setDraftMsg(d.message); setPanel({ type: "review", data: d }); };
  const openTrack = (c) => setPanel({ type: "track", data: c });
  const openCraft = (m) => { setCraftResult(null); setDraftMsg(""); setCraftTone(TONES[0]); setPanel({ type: "craft", data: m }); };

  const saveMsg = async (id) => { await api.put(`/cards/${id}/message`, { message: draftMsg }); await load(); };

  const approve = async (card, method) => {
    setBusy(true);
    try {
      await api.post(`/cards/${card.id}/approve`, { delivery_method: method });
      if (navigator.vibrate) navigator.vibrate([40, 60, 40]); // Celebration Pulse
      setPanel(null); setPreviewId(null);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Could not send card."); }
    finally { setBusy(false); }
  };

  const craftCard = async () => {
    setBusy(true);
    try {
      const m = panel.data;
      const res = await api.post("/cards/generate", { loved_one_id: m.loved_one_id, occasion: m.occasion, date: m.next_date, tone: craftTone });
      setCraftResult(res.data); setDraftMsg(res.data.message);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Generation failed."); }
    finally { setBusy(false); }
  };

  const runSweep = async () => { setBusy(true); try { await api.post("/drafts/sweep"); await load(); } finally { setBusy(false); } };

  const saveLovedOne = async () => {
    if (!nf.name.trim()) return;
    setSavingLo(true);
    try {
      const payload = {
        name: nf.name, relationship: nf.relationship, personality: nf.personality, interests: nf.interests,
        avatar_color: nf.avatar_color, pinterest_url: nf.pinterest_url, pinterest_linked: !!nf.pinterest_url,
        special_dates: nf.sd_date ? [{ label: nf.sd_label, date: nf.sd_date, recurring: true }] : [],
        date_of_birth: nf.dob || null,
      };
      await api.post("/loved-ones", payload);
      setAddOpen(false);
      setNf({ name: "", relationship: "", personality: "", interests: "", sd_label: "Birthday", sd_date: "", avatar_color: COLORS[0], pinterest_url: "", dob: "" });
      await load();
    } finally { setSavingLo(false); }
  };

  const firstName = user?.name?.split(" ")[0] || "there";
  const panelLo = panel?.data ? loById[panel.data.loved_one_id] : null;
  const highConf = !!(panelLo?.style_profile || panelLo?.pulse_alert);
  const glowNote = panelLo?.pulse_alert?.detail
    ? `Inspired by ${panelLo.name}'s ${panelLo.pulse_alert.type === "move" ? "recent move" : panelLo.pulse_alert.type || "recent update"}.`
    : "";

  return (
    <AppShell>
      {/* Hero */}
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <p className="text-xs text-muted-foreground tracking-[0.25em] uppercase mb-3">Your Curated Feed</p>
          <h1 className="font-serif text-5xl leading-tight">
            Welcome, <span className="gold-text italic">{firstName}</span>
          </h1>
          <p className="text-muted-foreground text-lg mt-2">A lookbook of moments worth celebrating.</p>
        </div>
        <div className="flex gap-3">
          <button data-testid="quick-sweep" onClick={runSweep} disabled={busy}
            className="flex items-center gap-2 surface rounded-2xl px-4 py-2.5 text-sm text-foreground hover:border-primary/40">
            {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />} Find Inspiration
          </button>
          <button data-testid="open-add-loved-one" onClick={() => setAddOpen(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white rounded-2xl px-5 py-2.5 hover:opacity-90">
            <Plus className="w-4 h-4" /> Add Loved One
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-24 text-muted-foreground">Curating your feed…</div>
      ) : (
        <MaisonWallet hasSentCards={cards.length > 0} />
      )}

      {loading ? null : lovedOnes.length === 0 ? (
        <div className="surface rounded-[2rem] p-16 text-center">
          <Heart className="w-12 h-12 mx-auto mb-4 text-secondary/70" />
          <h3 className="font-serif text-3xl mb-2">Begin your collection</h3>
          <p className="text-muted-foreground mb-6">Add someone special and we&apos;ll curate every occasion for you.</p>
          <button onClick={() => setAddOpen(true)} className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white px-6 py-3 rounded-2xl hover:opacity-90">
            <Plus className="w-4 h-4" /> Add a Loved One
          </button>
        </div>
      ) : (
        <>
          {/* Upcoming Occasions */}
          <section className="mb-14">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl">Upcoming Occasions</h2>
              <span className="text-sm text-muted-foreground">{upcoming.length} on the horizon</span>
            </div>
            {upcoming.length === 0 ? (
              <div className="surface rounded-[1.75rem] p-10 text-center text-muted-foreground">No occasions in the next 30 days.</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger" data-testid="upcoming-feed">
                {upcoming.map((m) => {
                  const draft = draftForMilestone(m);
                  return (
                    <div key={m.id} className="surface rounded-[1.75rem] overflow-hidden card-soft" data-testid="occasion-card">
                      <div className={`relative h-44 bg-gradient-to-br ${occasionTint(m.loved_one_name)} flex items-center justify-center`}>
                        {draft ? (
                          <img src={draft.image_thumb_base64 ? `data:image/jpeg;base64,${draft.image_thumb_base64}` : `data:image/png;base64,${draft.image_base64}`} alt="" className="absolute inset-0 w-full h-full object-cover" />
                        ) : (
                          <Cake className="w-12 h-12 text-white/80" style={{ color: m.avatar_color }} />
                        )}
                        <div className="absolute top-3 right-3 bg-white/85 backdrop-blur rounded-full px-3 py-1 text-xs font-medium text-foreground shadow-sm">
                          {m.days_until === 0 ? "Today" : `${m.days_until}d`}
                        </div>
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs uppercase tracking-wider gold-text">{m.occasion}</span>
                          {draft && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">Draft ready</span>}
                        </div>
                        <div className="font-serif text-xl mb-1">{m.loved_one_name}</div>
                        <div className="text-sm text-muted-foreground flex items-center gap-1.5 mb-4">
                          <Cake className="w-3.5 h-3.5" />
                          {new Date(m.next_date + "T00:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric" })}
                        </div>
                        <button
                          data-testid="occasion-cta"
                          onClick={() => draft ? openReview(draft) : openCraft(m)}
                          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-secondary text-white rounded-2xl py-3 hover:opacity-90"
                        >
                          {draft ? <>Review & Send <ArrowRight className="w-4 h-4" /></> : <>Craft Card <Wand2 className="w-4 h-4" /></>}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Recently Sent Gifts */}
          <section>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl">Recently Sent Gifts</h2>
            </div>
            {recentlySent.length === 0 ? (
              <div className="surface rounded-[1.75rem] p-10 text-center text-muted-foreground">Your sent gifts will appear here as a beautiful gallery.</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger" data-testid="sent-feed">
                {recentlySent.map((c) => (
                  <div key={c.id} className="surface rounded-[1.75rem] overflow-hidden card-soft cursor-pointer" data-testid="sent-card" onClick={() => openTrack(c)}>
                    <div className="h-40 overflow-hidden">
                      <img src={c.image_thumb_base64 ? `data:image/jpeg;base64,${c.image_thumb_base64}` : `data:image/png;base64,${c.image_base64}`} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">{c.occasion}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${c.delivery.status === "delivered" ? "bg-secondary/10 text-secondary border-secondary/30" : "bg-yellow-500/10 gold-text border-yellow-500/20"}`}>
                          {c.delivery.status === "delivered" ? "Delivered" : c.delivery.method === "digital" ? "Digital" : "In transit"}
                        </span>
                      </div>
                      <div className="font-serif text-lg">{c.loved_one_name}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* ===== Review Draft drawer ===== */}
      <SlideOver open={panel?.type === "review"} onClose={() => setPanel(null)}
        title="Review & Send" subtitle={panel?.data ? `${panel.data.loved_one_name} · ${panel.data.occasion}` : ""}>
        {panel?.type === "review" && panel?.data && (
          <div className="space-y-5">
            <div className="rounded-[1.5rem] overflow-hidden border border-border">
              <img src={`data:image/png;base64,${panel.data.image_base64}`} alt="" className="w-full h-56 object-cover" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm text-muted-foreground">Custom message</label>
                <MagicWand lovedOneId={panel.data.loved_one_id} occasion={panel.data.occasion} highConfidence={highConf} glowNote={glowNote} onPick={(t) => setDraftMsg(t)} />
              </div>
              <textarea data-testid="review-message" value={draftMsg} onChange={(e) => setDraftMsg(e.target.value)} rows={5}
                className="w-full bg-input border border-border rounded-2xl px-4 py-3 font-serif text-lg leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring/40 resize-none" />
              <button data-testid="review-save" onClick={() => saveMsg(panel.data.id)} className="mt-2 text-sm flex items-center gap-1.5 text-muted-foreground hover:text-foreground">
                <Save className="w-4 h-4" /> Save edits
              </button>
            </div>
            <button data-testid="review-preview-pdf" onClick={() => setPreviewId(panel.data.id)}
              className="w-full flex items-center justify-center gap-2 surface rounded-2xl py-3.5 text-primary hover:border-primary/40 font-medium">
              <FileText className="w-4 h-4" /> Preview PDF (see the unboxing)
            </button>
            <div className="flex gap-3">
              <button data-testid="review-digital" onClick={() => approve(panel.data, "digital")} disabled={busy}
                className="flex-1 flex items-center justify-center gap-2 surface rounded-2xl py-3.5 text-secondary hover:border-secondary/40"><Send className="w-4 h-4" /> Digital</button>
              <button data-testid="review-mail" onClick={() => approve(panel.data, "physical")} disabled={busy}
                className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-secondary to-primary text-white rounded-2xl py-3.5 hover:opacity-90"><Truck className="w-4 h-4" /> Approve & Mail</button>
            </div>
          </div>
        )}
      </SlideOver>

      {/* ===== Craft drawer ===== */}
      <SlideOver open={panel?.type === "craft"} onClose={() => setPanel(null)}
        title="Craft a Card" subtitle={panel?.data ? `${panel.data.loved_one_name} · ${panel.data.occasion}` : ""}>
        {panel?.type === "craft" && panel?.data && (
          <div className="space-y-5">
            {!craftResult ? (
              <>
                <div>
                  <label className="text-sm text-muted-foreground">Tone</label>
                  <select data-testid="craft-tone" value={craftTone} onChange={(e) => setCraftTone(e.target.value)}
                    className="w-full mt-1.5 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40">
                    {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <button data-testid="craft-generate" onClick={craftCard} disabled={busy}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-secondary text-white rounded-2xl py-4 hover:opacity-90 disabled:opacity-60">
                  {busy ? <><RefreshCw className="w-4 h-4 animate-spin" /> Crafting… (~20s)</> : <><Wand2 className="w-4 h-4" /> Generate Card</>}
                </button>
              </>
            ) : (
              <div className="space-y-5 animate-fade-up">
                <div className="rounded-[1.5rem] overflow-hidden border border-border">
                  <img src={`data:image/png;base64,${craftResult.image_base64}`} alt="" className="w-full h-56 object-cover" />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm text-muted-foreground">Message</label>
                  <MagicWand lovedOneId={panel.data.loved_one_id} occasion={panel.data.occasion} tone={craftTone} highConfidence={highConf} glowNote={glowNote} onPick={(t) => setDraftMsg(t)} />
                </div>
                <textarea data-testid="craft-message" value={draftMsg} onChange={(e) => setDraftMsg(e.target.value)} rows={5}
                  className="w-full bg-input border border-border rounded-2xl px-4 py-3 font-serif text-lg leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring/40 resize-none" />
                <button data-testid="craft-preview-pdf" onClick={async () => { await saveMsg(craftResult.id); setPreviewId(craftResult.id); }}
                  className="w-full flex items-center justify-center gap-2 surface rounded-2xl py-3 text-primary hover:border-primary/40 font-medium"><FileText className="w-4 h-4" /> Preview PDF</button>
                <div className="flex gap-3">
                  <button onClick={() => approve(craftResult, "digital")} disabled={busy} className="flex-1 flex items-center justify-center gap-2 surface rounded-2xl py-3 text-secondary hover:border-secondary/40"><Send className="w-4 h-4" /> Digital</button>
                  <button onClick={() => approve(craftResult, "physical")} disabled={busy} className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-secondary to-primary text-white rounded-2xl py-3 hover:opacity-90"><Truck className="w-4 h-4" /> Mail</button>
                </div>
              </div>
            )}
          </div>
        )}
      </SlideOver>

      {/* ===== Track drawer ===== */}
      <SlideOver open={panel?.type === "track"} onClose={() => setPanel(null)}
        title="Delivery" subtitle={panel?.data ? `${panel.data.loved_one_name} · ${panel.data.occasion}` : ""}>
        {panel?.type === "track" && panel?.data && (() => {
          const FLOW = [{ key: "created", label: "Created" }, { key: "printing", label: "Printing" }, { key: "mailed", label: "Mailed" }, { key: "in_transit", label: "In Transit" }, { key: "delivered", label: "Delivered" }];
          const delivery = panel.data.delivery || {};
          const isDigital = delivery.method === "digital";
          const idx = FLOW.findIndex((f) => f.key === delivery.status);
          return (
            <div className="space-y-6">
              <div className="rounded-[1.5rem] overflow-hidden border border-border">
                <img src={`data:image/png;base64,${panel.data.image_base64}`} alt="" className="w-full h-52 object-cover" />
              </div>
              <button data-testid="track-preview-pdf" onClick={() => setPreviewId(panel.data.id)}
                className="w-full flex items-center justify-center gap-2 surface rounded-2xl py-3 text-primary hover:border-primary/40 font-medium"><FileText className="w-4 h-4" /> Preview PDF</button>
              {isDigital ? (
                <div className="flex items-center gap-2 text-secondary"><CheckCircle2 className="w-5 h-5" /> Delivered digitally</div>
              ) : (
                <div className="space-y-3">
                  {FLOW.map((s, i) => (
                    <div key={s.key} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${i <= idx ? "bg-gradient-to-br from-primary to-secondary text-white" : "surface text-muted-foreground"}`}>
                        {i <= idx ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-2 h-2 rounded-full bg-current opacity-40" />}
                      </div>
                      <span className={i <= idx ? "text-foreground" : "text-muted-foreground"}>{s.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </SlideOver>

      {/* ===== Add Loved One drawer ===== */}
      <SlideOver open={addOpen} onClose={() => setAddOpen(false)} title="Add a Loved One" subtitle="They'll join your curated feed instantly">
        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground">Name *</label>
            <input data-testid="nf-name" value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })}
              className="w-full mt-1 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="e.g. Mom" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Relationship</label>
            <input data-testid="nf-relationship" value={nf.relationship} onChange={(e) => setNf({ ...nf, relationship: e.target.value })}
              className="w-full mt-1 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="Mother, best friend…" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Personality & interests</label>
            <textarea data-testid="nf-personality" value={nf.personality} onChange={(e) => setNf({ ...nf, personality: e.target.value })} rows={2}
              className="w-full mt-1 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40 resize-none" placeholder="Warm, loves the ocean…" />
            <input data-testid="nf-interests" value={nf.interests} onChange={(e) => setNf({ ...nf, interests: e.target.value })}
              className="w-full mt-2 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="Interests: gardening, jazz…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-muted-foreground">Occasion</label>
              <select data-testid="nf-sd-label" value={nf.sd_label} onChange={(e) => setNf({ ...nf, sd_label: e.target.value })}
                className="w-full mt-1 bg-input border border-border rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40">
                {["Birthday", "Anniversary", "Graduation", "Milestone", "Holiday"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Date</label>
              <input data-testid="nf-sd-date" type="date" value={nf.sd_date} onChange={(e) => setNf({ ...nf, sd_date: e.target.value })}
                className="w-full mt-1 bg-input border border-border rounded-2xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" />
            </div>
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Date of birth <span className="text-[11px] text-muted-foreground/70">(powers the Celestial Card)</span></label>
            <input data-testid="lo-dob" type="date" value={nf.dob} onChange={(e) => setNf({ ...nf, dob: e.target.value })}
              className="w-full mt-1 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Pinterest board URL (powers AI Shopper)</label>
            <input data-testid="nf-pinterest" value={nf.pinterest_url} onChange={(e) => setNf({ ...nf, pinterest_url: e.target.value })}
              className="w-full mt-1 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="https://pinterest.com/…" />
          </div>
          <button data-testid="nf-save" onClick={saveLovedOne} disabled={savingLo}
            className="w-full bg-gradient-to-r from-primary to-secondary text-white rounded-2xl py-3.5 hover:opacity-90 disabled:opacity-50">
            {savingLo ? "Adding…" : "Add to Collection"}
          </button>
          <button onClick={() => { setAddOpen(false); navigate("/loved-ones"); }} className="w-full text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> Add full details (address, more dates)
          </button>
        </div>
      </SlideOver>

      {/* Full-screen Gift Preview */}
      <GiftPreview cardId={previewId} open={!!previewId} onClose={() => setPreviewId(null)}
        onSend={previewId ? () => { const c = cards.find((x) => x.id === previewId) || craftResult; if (c) approve(c, "physical"); } : null} />
    </AppShell>
  );
};

export default CommandCenter;
